'use client';
import IconArrowLeft from '@/assets/icons/icon-arrow-left.svg';
import IconArrowRight from '@/assets/icons/icon-arrow-right.svg';
import { useNewArrivalsQuery } from '@/graphql';
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import { useEffect, useRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import CategoryCard from '../category-card';
import styles from './new-arrival.module.scss';
import { useLanguageCode } from '@/hooks/useLanguageCode';

const NewArrival = ({ title }: any) => {
  const splideRef = useRef<any>(null);
  const languageCode = useLanguageCode();

  const { data, loading } = useNewArrivalsQuery({
    variables: {
      first: 100,
      languageCode: languageCode,
    },
  });

  useEffect(() => {
    const splide = splideRef.current?.splide;

    const bar = splide?.root?.querySelector(
      `.${styles['my-slider-progress-bar']}`
    );

    // Function to update the progress bar
    const updateProgressBar = () => {
      const end = splide?.Components?.Controller.getEnd() + 1;
      const rate = Math.min((splide?.index + 1) / end, 1);
      if (bar) {
        bar.style.width = `${100 * rate}%`;
      }
    };

    // Update the progress bar immediately on mount
    updateProgressBar();

    // Add event listeners for 'mounted' and 'move' events
    splide?.on('mounted move', updateProgressBar);

    return () => {
      // Cleanup event listeners when component unmounts
      splide?.off('mounted move', updateProgressBar);
    };
  }, []);

  const goToNextSlide = () => {
    const splide = splideRef.current?.splide;
    if (splide) {
      splide.go('>');
    }
  };
  const goToPrevSlide = () => {
    const splide = splideRef.current?.splide;
    if (splide) {
      splide.go('<');
    }
  };

  return data?.newArrivals?.edges && data?.newArrivals?.edges.length > 0 ? (
    <div className={styles.carouselBestBuy}>
      <div className={styles.customHeader}>
        <h3>{title}</h3>
        <div className={styles.customBtns}>
          <button
            type="button"
            className={styles.carouselNextButton}
            onClick={goToPrevSlide}
          >
            {' '}
            <IconArrowLeft />
          </button>
          <button
            type="button"
            className={styles.carouselNextButton}
            onClick={goToNextSlide}
          >
            {' '}
            <IconArrowRight />
          </button>
        </div>
      </div>
      <Splide
        ref={splideRef}
        options={{
          updateOnMove: true,
          fixedWidth: 'auto',
          perPage: 4,
          perMove: 1,
          rewind: true,
          arrows: false,
          pagination: false,
          gap: '12px',
          breakpoints: {
            1024: {
              perPage: 3,
            },
            768: {
              perPage: 3,
              gap: '8px',
            },
            480: {
              perPage: 1,
              gap: '8px',
            },
          },
        }}
        hasTrack={false}
        style={{ padding: '0' }}
      >
        <SplideTrack>
          {loading
            ? Array.from({ length: 10 }).map((_, index) => (
                <SplideSlide key={index}>
                  <div className={`${styles.skeleton}`}>
                    <Skeleton />
                  </div>
                </SplideSlide>
              ))
            : data?.newArrivals?.edges?.map((p: any) => {
                const labels =
                  p?.node?.variant?.labels?.edges?.map(
                    (edge: any) => edge?.node
                  ) || [];

                return (
                  <SplideSlide key={p?.node?.id}>
                    <CategoryCard
                      id={p?.node?.variant?.id}
                      productId={p?.node?.variant?.product?.id}
                      categorySlug={p?.node?.category?.slug}
                      slug={p?.node?.variant?.product?.slug}
                      image={p?.node?.variant?.media[0]?.url}
                      wide={false}
                      productType={p?.node?.variant?.product?.category?.name}
                      labels={labels}
                      availability={p?.node?.variant?.availability}
                      title={p?.node?.variant?.name}
                      pricePerMonth={
                        p?.node?.variant?.pricing?.installmentPrice12?.amount
                      }
                      price={p?.node?.variant?.pricing?.price?.net?.amount ?? 0}
                      priceUndiscounted={
                        p?.node?.variant?.pricing?.priceUndiscounted?.net
                          ?.amount ?? 0
                      }
                      discount={
                        p?.node?.variant?.pricing?.discount?.net?.amount ?? 0
                      }
                      bonus={p?.node?.variant?.pricing?.bonusesAmount}
                      className={styles.categoryCard}
                    />
                  </SplideSlide>
                );
              })}
        </SplideTrack>
        <div className={styles['my-slider-progress']}>
          <div className={styles['my-slider-progress-bar']}></div>
        </div>
      </Splide>
    </div>
  ) : null;
};

export default NewArrival;
